import { api } from './api';

export const AFFILIATE_ATTRIBUTION_KEY = 'pintarplay_affiliate_attribution_v1';
export const AFFILIATE_PENDING_ATTRIBUTION_KEY = 'pintarplay_affiliate_pending_v1';

const PENDING_VERSION = 1;
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const INITIAL_RETRY_DELAY_MS = 30 * 1000;
const MAX_RETRY_DELAY_MS = 60 * 60 * 1000;

function clearAffiliateToken() {
  localStorage.removeItem(AFFILIATE_ATTRIBUTION_KEY);
}

function clearPendingAttribution() {
  localStorage.removeItem(AFFILIATE_PENDING_ATTRIBUTION_KEY);
}

export function clearAffiliateAttribution() {
  clearAffiliateToken();
  clearPendingAttribution();
}

export function getAffiliateAttributionToken(now = Date.now()) {
  try {
    const saved = JSON.parse(localStorage.getItem(AFFILIATE_ATTRIBUTION_KEY));
    const expiresAt = new Date(saved?.expiresAt).getTime();
    if (!saved?.token || !Number.isFinite(expiresAt) || expiresAt <= now) {
      // An expired token must not erase an unrelated pending referral retry.
      clearAffiliateToken();
      return null;
    }
    return saved.token;
  } catch {
    clearAffiliateToken();
    return null;
  }
}

function readPendingAttribution(now) {
  try {
    const pending = JSON.parse(
      localStorage.getItem(AFFILIATE_PENDING_ATTRIBUTION_KEY),
    );
    if (
      pending?.version !== PENDING_VERSION
      || typeof pending.code !== 'string'
      || !pending.code
      || !Number.isFinite(pending.createdAt)
      || pending.createdAt + ATTRIBUTION_TTL_MS <= now
    ) {
      clearPendingAttribution();
      return null;
    }
    return {
      ...pending,
      attempts: Number.isInteger(pending.attempts) && pending.attempts >= 0
        ? pending.attempts
        : 0,
      nextAttemptAt: Number.isFinite(pending.nextAttemptAt)
        ? pending.nextAttemptAt
        : pending.createdAt,
    };
  } catch {
    clearPendingAttribution();
    return null;
  }
}

export function getAffiliateAttributionRetryDelay(now = Date.now()) {
  const pending = readPendingAttribution(now);
  return pending
    ? Math.max(0, pending.nextAttemptAt - now)
    : null;
}

function writePendingAttribution(pending) {
  try {
    localStorage.setItem(
      AFFILIATE_PENDING_ATTRIBUTION_KEY,
      JSON.stringify(pending),
    );
    return true;
  } catch {
    return false;
  }
}

function removeReferralFromVisibleUrl(url) {
  url.searchParams.delete('ref');
  window.history.replaceState(
    window.history.state,
    document.title,
    `${url.pathname}${url.search}${url.hash}`,
  );
}

function isDefinitiveRejection(error) {
  const status = Number(error?.response?.status);
  return status >= 400
    && status < 500
    && status !== 408
    && status !== 425
    && status !== 429;
}

function retryDelay(attempts) {
  return Math.min(
    MAX_RETRY_DELAY_MS,
    INITIAL_RETRY_DELAY_MS * (2 ** Math.min(attempts - 1, 7)),
  );
}

function retainPendingAfterTransientFailure(pending, now) {
  const attempts = pending.attempts + 1;
  writePendingAttribution({
    ...pending,
    attempts,
    nextAttemptAt: now + retryDelay(attempts),
  });
}

async function captureReferral(locationLike, now) {
  const url = new URL(locationLike.href);
  const code = (url.searchParams.get('ref') || '').trim();
  const hasVisibleCode = Boolean(code);

  // Expired signed tokens are removed, but a still-valid token survives any
  // retryable attempt to replace it.
  getAffiliateAttributionToken(now);

  let pending;
  if (hasVisibleCode) {
    pending = {
      version: PENDING_VERSION,
      code,
      createdAt: now,
      attempts: 0,
      nextAttemptAt: now,
    };

    // The code leaves the address bar only after it is durable locally. If
    // storage fails, the visible URL remains the retry mechanism.
    if (writePendingAttribution(pending)) {
      removeReferralFromVisibleUrl(url);
    }
  } else {
    pending = readPendingAttribution(now);
  }

  if (!pending) {
    return false;
  }

  // One network request per invocation plus persisted backoff prevents online
  // events or repeated mounts from creating a retry loop.
  if (!hasVisibleCode && pending.nextAttemptAt > now) {
    return false;
  }

  try {
    const response = await api.post('/affiliate/attribution', {
      code: pending.code,
    });
    const expiresAt = response.data?.expiresAt;
    const token = response.data?.token;
    const serverExpiry = new Date(expiresAt).getTime();
    if (!token || !Number.isFinite(serverExpiry) || serverExpiry <= now) {
      retainPendingAfterTransientFailure(pending, now);
      return false;
    }

    // Never retain a client-side attribution beyond the 30-day policy even if
    // a malformed response advertises a later expiry.
    const clientExpiry = new Date(
      Math.min(serverExpiry, now + ATTRIBUTION_TTL_MS),
    ).toISOString();
    try {
      localStorage.setItem(
        AFFILIATE_ATTRIBUTION_KEY,
        JSON.stringify({ token, expiresAt: clientExpiry }),
      );
    } catch {
      retainPendingAfterTransientFailure(pending, now);
      return false;
    }
    clearPendingAttribution();
    return true;
  } catch (error) {
    if (isDefinitiveRejection(error)) {
      clearAffiliateAttribution();
      if (hasVisibleCode) removeReferralFromVisibleUrl(url);
      return false;
    }
    retainPendingAfterTransientFailure(pending, now);
    return false;
  }
}

let captureInFlight = null;

export function captureReferralFromLocation(
  locationLike = window.location,
  { now = Date.now() } = {},
) {
  if (captureInFlight) return captureInFlight;
  captureInFlight = captureReferral(locationLike, now)
    .finally(() => {
      captureInFlight = null;
    });
  return captureInFlight;
}
